import { useCallback, useContext, useMemo } from 'react';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { ChatContextType } from '@trinity/chat';
import type { Chat } from '@trinity/chat';
import { useAttachmentContext } from './context';
import { ChatStateDispatchContext } from '../Chat.State/context';
import { ActionsContext, ActionsContextValue, ActiveChatSessionContext } from '../Chat/context';

function useBuildOpenNewSubjectForTableAction() {
  const dispatch = useContext(ChatStateDispatchContext);
  const chat = useContext(ActiveChatSessionContext);
  const brandIdentifier = useActiveBrandIdentifier();

  return useCallback((row: Chat.AttachmentTableRow, action: Chat.Attachment.SelectProfileItem) => () => {
    const hasIdField = action.idField in row;
    const hasNameField = action.nameField in row;

    if (!hasIdField || !hasNameField) {
      return null;
    }

    const idField = row[action.idField];
    const nameField = row[action.nameField];

    const idValue = typeof idField === 'object' ? idField.value : idField;
    const nameValue = typeof nameField === 'object' ? nameField.value : nameField;

    const subject = {
      id: idValue,
      name: nameValue,
    };
    const initialUserMessage = action.initialMessageTemplate ? action.initialMessageTemplate.replace('{name}', subject.name) : `Deep dive into ${subject.name}`;

    if (chat.context.type !== ChatContextType.Agent) {
      throw new Error('Cannot open new chat for subject when chat context is not Agent');
    }

    dispatch({
      type: 'new-chat-started',
      payload: {
        brandIdentifier,
        context: {
          ...chat.context,
          data: {
            ...chat.context.data,
            subject,
          },
        },
        pendingQuery: initialUserMessage,
      },
    });
  }, [dispatch, chat.context, brandIdentifier]);
}

const useRowLevelActions = (row: Chat.AttachmentTableRow): ActionsContextValue => {

  const tableAttachment = useAttachmentContext<Chat.Attachment.Table>();

  const buildOpenNewSubjectForTableAction = useBuildOpenNewSubjectForTableAction();

  return useMemo(() => {
    const actions: ActionsContextValue = {};

    const actionKeys = Object.keys(tableAttachment.data.actions ?? {});

    for (const actionKey of actionKeys) {
      const action = tableAttachment.data.actions[actionKey];

      if (action.type === 'open-new-tab-for-subject') {
        const actionCallback = buildOpenNewSubjectForTableAction(row, action);

        if (!actionCallback) {
          continue;
        }
        actions[actionKey] = actionCallback;
      }
    }

    return actions;
  }, [tableAttachment.data.actions, buildOpenNewSubjectForTableAction, row]);
};

type Props = {
  row: Chat.AttachmentTableRow;
  children: React.ReactNode;
};

export const TableActionsContainer = (props: Props) => {
  const actions = useRowLevelActions(props.row);

  return (
    <ActionsContext.Provider value={actions}>
      {props.children}
    </ActionsContext.Provider>
  );
};